import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

/**
 * TableSkeleton - Skeleton loading state for tables
 * 
 * Provides:
 * - Configurable number of rows and columns
 * - Optional header row
 * - Consistent animation and styling
 * 
 * @example
 * <TableSkeleton rows={5} columns={4} showHeader />
 */
const TableSkeleton = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  className,
}: TableSkeletonProps) => {
  return (
    <div className={cn("w-full space-y-3", className)}>
      {/* Header row */}
      {showHeader && (
        <div className="flex gap-4 pb-2 border-b border-border">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton
              key={`header-${i}`}
              className={cn(
                "h-4",
                i === 0 ? "w-32" : "w-24",
                "flex-shrink-0"
              )}
            />
          ))}
        </div>
      )}

      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          className="flex gap-4 py-3 border-b border-border last:border-b-0"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              className={cn(
                "h-5",
                // Vary widths for more realistic appearance
                colIndex === 0
                  ? "w-40"
                  : colIndex === columns - 1
                  ? "w-20"
                  : "w-28",
                "flex-shrink-0"
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default TableSkeleton;
